
export const ORDER_STATUS = {
    WAITING: 'WAITING',
    PRODUCTION: 'PRODUCTION',
    REVIEW: 'REVIEW',
    AWAITING_CLIENT_APPROVAL: 'AWAITING_CLIENT_APPROVAL',
    SUBMITTED: 'SUBMITTED',
    PROCESSING: 'PROCESSING',
    PUBLISHED: 'PUBLISHED',
    INVOICE: 'INVOICE',
    COMPLETED: 'COMPLETED',
    CANCELED: 'CANCELED',
};

export const ITEM_STATUS = {
    PRODUCTION: 'PRODUCTION',
    AWAITING_CLIENT_APPROVAL: 'AWAITING_CLIENT_APPROVAL',
    REVIEW: 'REVIEW',
    SUBMITTED: 'SUBMITTED',
    PROCESSING: 'PROCESSING',
    PUBLISHED: 'PUBLISHED',
    CANCELED: 'CANCELED'
};

/**
 * Get status label for display
 */
export function getStatusLabel(status, $t) {
    const statusLabels = {
        [ORDER_STATUS.WAITING]: $t('Awaiting Payment Confirmation'),
        [ORDER_STATUS.PRODUCTION]: $t('Content in Review'),
        [ORDER_STATUS.REVIEW]: $t('Article Rejected'),
        [ORDER_STATUS.AWAITING_CLIENT_APPROVAL]: $t('Awaiting Client Approval'),
        [ORDER_STATUS.SUBMITTED]: $t('Send to Portal'),
        [ORDER_STATUS.PROCESSING]: $t('Awaiting Publication'),
        [ORDER_STATUS.PUBLISHED]: $t('Published'),
        [ORDER_STATUS.INVOICE]: $t('Invoice'),
        [ORDER_STATUS.COMPLETED]: $t('Completed'),
        [ORDER_STATUS.CANCELED]: $t('Canceled'),
        // Item status labels (using same constants)
        [ITEM_STATUS.PRODUCTION]: $t('Content in Review'),
        [ITEM_STATUS.AWAITING_CLIENT_APPROVAL]: $t('Awaiting Client Approval'),
        [ITEM_STATUS.REVIEW]: $t('Article Rejected'),
        [ITEM_STATUS.SUBMITTED]: $t('Send to Portal'),
        [ITEM_STATUS.PROCESSING]: $t('Awaiting Publication'),
        [ITEM_STATUS.PUBLISHED]: $t('Published'),
        [ITEM_STATUS.CANCELED]: $t('Canceled')
    };

    return statusLabels[status] || status;
}

/**
 * Get status severity for UI components
 */
export function getStatusSeverity(status) {
    switch (status) {
        case ORDER_STATUS.WAITING:
            return 'warn';
        case ORDER_STATUS.PRODUCTION:
        case ITEM_STATUS.PRODUCTION:
            return 'warn';
        case ORDER_STATUS.REVIEW:
            return 'danger';
        case ORDER_STATUS.AWAITING_CLIENT_APPROVAL:
        case ITEM_STATUS.AWAITING_CLIENT_APPROVAL:
            return 'warn';
        case ORDER_STATUS.SUBMITTED:
        case ITEM_STATUS.SUBMITTED:
            return 'info';
        case ORDER_STATUS.PROCESSING:
        case ITEM_STATUS.PROCESSING:
            return 'info';
        case ORDER_STATUS.PUBLISHED:
        case ITEM_STATUS.PUBLISHED:
            return 'success';
        case ORDER_STATUS.INVOICE:
            return 'info';
        case ORDER_STATUS.COMPLETED:
            return 'success';
        case ORDER_STATUS.CANCELED:
            return 'danger';
        default:
            return 'secondary';
    }
}

/**
 * Build the cancellation tooltip text (reason + date) for a canceled order.
 * Returns null when the order is not canceled, so v-tooltip stays disabled.
 */
export function getCancellationTooltip(order, $t) {
    if (!order || order.status !== ORDER_STATUS.CANCELED) {
        return null;
    }

    const parts = [];

    if (order.canceled_at) {
        parts.push(`${$t('Canceled at')}: ${new Date(order.canceled_at).toLocaleString()}`);
    }

    parts.push(`${$t('Cancellation reason')}: ${order.cancellation_reason || $t('No reason provided')}`);

    return parts.join('\n');
}

/**
 * Get item dynamic status helper
 */
export function getItemStatus(item) {
    if (item?.canceled_at) return ITEM_STATUS.CANCELED;
    return item.link_status || ITEM_STATUS.PRODUCTION;
}

/**
 * Main composable function
 */
export function useOrderStatus() {
    // Whether an order is still open for item add/cancel: not canceled, invoice
    // not yet emitted, and not already settled through an automatic gateway.
    const orderEditable = (order) => {
        if (!order || order.status === ORDER_STATUS.CANCELED) return false;
        // Once an invoice has been generated, the order is closed for item changes.
        if (order.invoice || order.invoice_id) return false;
        const gatewayPaid = ['stripe', 'pix', 'boleto', 'wise'].includes(order.payment_method) && !!order.payment_date;
        return !gatewayPaid;
    };

    return {
        ORDER_STATUS,
        ITEM_STATUS,
        getStatusLabel,
        getStatusSeverity,
        getItemStatus,
        getCancellationTooltip,
        canCancelOrder: (order) => {
            if (!order || order.status === ORDER_STATUS.CANCELED) return false;
            const initialInvoiceEmitted = !!order.invoice && (order.invoice.initial_invoice_emitted === true || order.invoice.initial_invoice_emitted === 1);
            if (order.status === ORDER_STATUS.COMPLETED && !initialInvoiceEmitted) return false;
            return true;
        },
        canEditItems: (order) => orderEditable(order),
        canCancelItem: (order, item, activeItemCount) => {
            if (!orderEditable(order)) return false;
            if (!item || item.canceled_at) return false;
            if (typeof activeItemCount === 'number' && activeItemCount <= 1) return false;
            return true;
        },
    };
}
